import { LayerName } from '@3d-scene/manager/layer-manager';
import { ProjectModel } from '@project/model/project.model';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Box3, Group, Object3D, Vector3 } from 'three';
import { ArticleUtils } from '@article/util/article-utils';
import { IsProjectType } from '@project/model/is-project-type';

export abstract class BaseLayerElementCreator {

  protected static readonly X_DELIMITER: string = 'x ';

  constructor(protected projectModel: ProjectModel, public readonly layerName: LayerName) {
  }

  public abstract createLayerElements(): Group;

  public onActivate(): void {}

  public onDeactivate(): void {}

  protected findBoundingPointsInGlobal(article: ArticleContainer): Vector3[] {
    const result: Vector3[] = [];
    const model: Box3 = article.bounds;

    const points: Vector3[] = [
      new Vector3(model.max.x, model.max.y, model.max.z),
      new Vector3(model.min.x, model.max.y, model.max.z),
      new Vector3(model.min.x, model.max.y, model.min.z),
      new Vector3(model.max.x, model.max.y, model.min.z),
      new Vector3(model.min.x, model.min.y, model.min.z),
      new Vector3(model.min.x, model.min.y, model.max.z),
      new Vector3(model.min.x, model.min.y, model.min.z),
      new Vector3(model.max.x, model.min.y, model.min.z)
    ];

    for (const point of points) {
      result.push(MathUtils.localToGlobal(article, point));
    }

    return result;
  }

  protected isIncludedInCalculation(article: ArticleContainer): boolean {
    return article.isExportChildren() && !(IsProjectType.mcsGds(this.projectModel.currentProject) && ArticleUtils.isMountingRail(article.article));
  }

  protected calculateMinGlobalArticlePoint(article: ArticleContainer): Vector3 {
    const minArticlePoint: Vector3 = MathUtils.localToGlobal(article, article.bounds.min);
    const maxArticlePoint: Vector3 = MathUtils.localToGlobal(article, article.bounds.max);
    return new Vector3(
      Math.max(minArticlePoint.x, maxArticlePoint.x),
      Math.min(minArticlePoint.y, maxArticlePoint.y),
      Math.min(minArticlePoint.z, maxArticlePoint.z));
  }

  protected calculateMinGlobalPointOfArticles(articles: ArticleContainer[]): Vector3 {
    const minArticlePoints: Vector3[] = articles.map(article => MathUtils.localToGlobal(article, article.bounds.min));
    const maxArticlePoints: Vector3[] = articles.map(article => MathUtils.localToGlobal(article, article.bounds.max));
    const xAxisBounds: number[] = minArticlePoints.map(point => point.x).concat(maxArticlePoints.map(point => point.x));
    const yAxisBounds: number[] = minArticlePoints.map(point => point.y).concat(maxArticlePoints.map(point => point.y));
    const zAxisBounds: number[] = minArticlePoints.map(point => point.z).concat(maxArticlePoints.map(point => point.z));
    return new Vector3(
      Math.max(...xAxisBounds),
      Math.min(...yAxisBounds),
      Math.min(...zAxisBounds));
  }

  protected calculateMaxGlobalArticlePoint(article: ArticleContainer): Vector3 {
    const minArticlePoint: Vector3 = MathUtils.localToGlobal(article, article.bounds.min);
    const maxArticlePoint: Vector3 = MathUtils.localToGlobal(article, article.bounds.max);
    return new Vector3(
      Math.min(minArticlePoint.x, maxArticlePoint.x),
      Math.max(minArticlePoint.y, maxArticlePoint.y),
      Math.max(minArticlePoint.z, maxArticlePoint.z));
  }

  protected calculateMaxGlobalPointOfArticles(articles: ArticleContainer[]): Vector3 {
    const minArticlePoints: Vector3[] = articles.map(article => MathUtils.localToGlobal(article, article.bounds.min));
    const maxArticlePoints: Vector3[] = articles.map(article => MathUtils.localToGlobal(article, article.bounds.max));
    const xAxisBounds: number[] = minArticlePoints.map(point => point.x).concat(maxArticlePoints.map(point => point.x));
    const yAxisBounds: number[] = minArticlePoints.map(point => point.y).concat(maxArticlePoints.map(point => point.y));
    const zAxisBounds: number[] = minArticlePoints.map(point => point.z).concat(maxArticlePoints.map(point => point.z));
    return new Vector3(
      Math.min(...xAxisBounds),
      Math.max(...yAxisBounds),
      Math.max(...zAxisBounds));
  }

  protected createLayerGroup(elements: Object3D[] = []): Group {
    const group = new Group();
    if(elements.length > 0) {
      group.add(...elements);
    }
    group.name = this.layerName;
    return group;
  }
}
